<template>
    <Draggable
        tag="div"
        class="rt-head"
        :style="{ gridTemplateColumns: template, minWidth: minWidth }"
        :value="columns"
        handle=".rt-head__grip"
        :move="onMove"
        @input="onReorder"
    >
        <div
            class="rt-head__cell"
            :class="cellClass(column, index)"
            :style="cellStyle(index)"
            :key="column.key"
            @click="onSort(column)"
            v-for="(column, index) in columns"
        >
            <!-- Ручка вынута из потока и висит в левом отступе ячейки: иначе она
                 сдвигала бы название вправо относительно значений в строках. -->
            <span class="rt-head__grip" @click.stop v-if="!column.orderLocked">⋮⋮</span>
            <span class="rt-head__title" :title="column.title">{{ column.title }}</span>
            <span class="rt-head__arrow" v-if="isActive(column)">{{ sort.reverse ? '▼' : '▲' }}</span>
        </div>
    </Draggable>
</template>
<script>
import Draggable from 'vuedraggable';

import { gridTemplate, gridMinWidthCss, stickyOffsets, stickyOffsetCss, lastStickyIndex } from './requestRowData';

export default {
    components: { Draggable },
    props: {
        columns: { type: Array, required: true },
        sort: { type: Object, required: true },
    },
    computed: {
        template() {
            return gridTemplate(this.columns);
        },
        minWidth() {
            return gridMinWidthCss(this.columns);
        },
        offsets() {
            return stickyOffsets(this.columns);
        },
        lastSticky() {
            return lastStickyIndex(this.columns);
        },
    },
    methods: {
        cellClass(column, index) {
            return {
                'rt-head__cell_sortable': column.sortType !== null,
                'rt-head__cell_on': this.isActive(column),
                'rt-head__cell_sticky': this.offsets[index] !== null,
                'rt-head__cell_sticky-last': index === this.lastSticky,
            };
        },
        cellStyle(index) {
            const offset = this.offsets[index];
            return offset === null ? {} : { left: stickyOffsetCss(offset, index) };
        },
        isActive(column) {
            return column.sortType !== null && column.sortType === this.sort.type;
        },
        onSort(column) {
            if (column.sortType === null) return;
            const reverse = this.isActive(column) ? !this.sort.reverse : true;
            this.$emit('sort', { type: column.sortType, reverse });
        },
        // Указатель не должен обещать позицию, которую pinnedFirst тут же отменит:
        // тащить можно только внутри своей группы и не поверх заблокированного №.
        onMove(event) {
            const dragged = event.draggedContext.element;
            const related = event.relatedContext.element;

            if (!related) return false;

            return Boolean(dragged.pinned) === Boolean(related.pinned) && !related.orderLocked;
        },
        onReorder(columns) {
            this.$emit(
                'reorder',
                columns.map((column) => column.key),
            );
        },
    },
};
</script>
<style lang="scss">
.rt-head {
    display: grid;
    position: sticky;
    top: 0;
    z-index: $layer-sticky-head;
    // Шапка липкая по вертикали, но фона у неё не было — строки проезжали сквозь неё.
    background: #fff;

    &__cell {
        display: flex;
        position: relative;
        align-items: center;
        background: inherit;

        &_sticky {
            position: sticky;
            z-index: $layer-sticky-cell;
        }

        &_sticky-last {
            border-right: 1px solid #e8eaed;
        }
        // Парная величина к .rt-row__cell — заголовок обязан стоять ровно над
        // значением, иначе колонки визуально разъедутся.
        padding: 0 10px;
        height: 36px;
        overflow: hidden;
        font: 600 0.6875rem/1 var(--font-family);
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #9aa0a6;
        white-space: nowrap;
        user-select: none;

        // Парная величина к .rt-row__cell:last-child — заголовок обязан стоять
        // ровно над значением.
        &:last-child {
            padding-left: 20px;
        }

        &_sortable {
            cursor: pointer;
        }

        &_on {
            color: #2e7d32;
        }

        &:hover .rt-head__grip {
            opacity: 1;
        }
    }

    // Ручка вынута из потока и висит в левом отступе ячейки: в потоке она сдвигала бы
    // название вправо относительно значения под ним, а № приходилось держать распоркой.
    //
    // Отступ 10px (был 12px), ⋮⋮ шрифтом ячейки занял бы из них 8.6px — почти без
    // просвета, ручка липла бы к названию. Уменьшенный кегль и своя разрядка дают
    // 6.6px, и прижатая к краю ручка отбивается от названия на 2.4px (было 4.4px —
    // не измерено заново в браузере после сокращения паддинга, стоит перепроверить
    // визуально при следующей правке этой ячейки).
    &__grip {
        position: absolute;
        left: 1px;
        top: 16px;
        transform: translateY(-50%);
        font-size: 10px;
        letter-spacing: 0;
        opacity: 0;
        cursor: grab;
        transition: opacity 0.15s;

        &:active {
            cursor: grabbing;
        }
    }

    &__title {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__arrow {
        flex-shrink: 0;
        margin-left: 4px;
        font-size: 9px;
    }
}
</style>
